import { createContext, useContext, useEffect, useRef, useState } from "react";
import { supabase, startEmailVerification } from "../supabase";

const UserContext = createContext();

const REMEMBER_ME_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days
const SHORT_SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 hours

export function useUser() {
  return useContext(UserContext);
}

export function UserProvider(props) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  // Tracks the signed-in user id outside of React state so the
  // onAuthStateChange listener (registered once, in a [] effect) can
  // recognize a redundant SIGNED_IN for the same user without depending
  // on stale closure state.
  const signedInUserIdRef = useRef(null);

  async function login(email, password, rememberMe = false) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check if email is verified
      if (data.user && data.user.email_confirmed_at) {
        if (rememberMe) {
          const expiryTime = Date.now() + REMEMBER_ME_DURATION;
          localStorage.setItem("pb_rememberMeExpiry", expiryTime.toString());
          localStorage.setItem("pb_isRemembered", "true");
        } else {
          const expiryTime = Date.now() + SHORT_SESSION_DURATION;
          localStorage.setItem("pb_rememberMeExpiry", expiryTime.toString());
          localStorage.setItem("pb_isRemembered", "false");
        }
        setVerified(true);
        signedInUserIdRef.current = data.user.id;
        setUser({
          email: data.user.email,
          id: data.user.id,
          name: data.user.user_metadata?.name || data.user.email
        });
        setToken(data.session?.access_token || null);
        return { verified: true };
      } else {
        await supabase.auth.signOut();
        signedInUserIdRef.current = null;
        setUser(null);
        return { verified: false };
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  }

  async function logout() {
    localStorage.removeItem("pb_rememberMeExpiry");
    localStorage.removeItem("pb_isRemembered");
    await supabase.auth.signOut();
    signedInUserIdRef.current = null;
    setUser(null);
    setToken(null);
  }

  async function register(email, password, name) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name,
          },
          emailRedirectTo: `${window.location.origin}/verifyEmail`,
        },
      });

      if (error) throw error;

      // Sign out immediately after registration to force email verification
      await supabase.auth.signOut();

      return {
        success: true,
        message:
          "Registration successful! Please check your email to verify your account.",
      };
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        message: error.message || "Registration failed. Please try again.",
      };
    }
  }

  async function init() {
    try {
      const rememberMeExpiry = localStorage.getItem("pb_rememberMeExpiry");
      const isRemembered = localStorage.getItem("pb_isRemembered") === "true";
      
      if (rememberMeExpiry) {
        const expiryTime = parseInt(rememberMeExpiry);
        const now = Date.now();

        if (now > expiryTime) {
          await supabase.auth.signOut();
          signedInUserIdRef.current = null;
          setUser(null);
          setToken(null);
          return;
        }
      }

      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) throw error;

      if (session?.user) {
        const isEmailVerified = !!session.user.email_confirmed_at;
        if (isEmailVerified) {
          setVerified(true);
          signedInUserIdRef.current = session.user.id;
          setUser({
            email: session.user.email,
            id: session.user.id,
            name: session.user.user_metadata?.name || session.user.email
          });
          setToken(session.access_token);
        } else {
          await supabase.auth.signOut();
          signedInUserIdRef.current = null;
          setUser(null);
          setToken(null);
        }
      } else {
        signedInUserIdRef.current = null;
        setUser(null);
        setToken(null);
      }

      if (!isRemembered && session?.user) {
        updateSessionActivity();
      }
    } catch (err) {
      console.error("Init error:", err);
      signedInUserIdRef.current = null;
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }

  const updateSessionActivity = () => {
    const isRemembered = localStorage.getItem("pb_isRemembered") === "true";

    if (!isRemembered) {
      const newExpiryTime = Date.now() + SHORT_SESSION_DURATION;
      localStorage.setItem("pb_rememberMeExpiry", newExpiryTime.toString());
    }
  };

  useEffect(() => {
    async function getToken() {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setToken(session.access_token);
      }
    }
    if (user !== null) {
      getToken();
    }
  }, [user]);

  useEffect(() => {
    async function getUser() {
      await init();
    }
    getUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // Supabase re-fires SIGNED_IN for the same session when the
          // window/tab regains focus. Skip it: calling setUser here with
          // a fresh object literal would otherwise look like a real user
          // change to consumers (e.g. LeaguesProvider), triggering an
          // unnecessary refetch.
          if (signedInUserIdRef.current === session.user.id) {
            return;
          }
          const isEmailVerified = !!session.user.email_confirmed_at;
          if (isEmailVerified) {
            setVerified(true);
            signedInUserIdRef.current = session.user.id;
            setUser({
              email: session.user.email,
              id: session.user.id,
              name: session.user.user_metadata?.name || session.user.email
            });
            setToken(session.access_token);
          }
        } else if (event === 'SIGNED_OUT') {
          signedInUserIdRef.current = null;
          setUser(null);
          setToken(null);
          setVerified(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <UserContext.Provider
      value={{
        current: user,
        token: token,
        loading: loading,
        verified: verified,
        login,
        logout,
        register,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
}
